// Hideo - Scripts page (Tools › Scripts, src/pages/ScriptsPage.vue): every script of the profile, including those left
// without a video, with search, an "All / No video" filter, words, spoken duration, tabs and last edit; split in two
// lists: scripts still in progress (video not published, or no video) and scripts of published videos, by year → month; "Attach" gives a
// script without a video to a video (src/components/ScriptLinkDialog.vue); the user's saved templates, with rename and
// delete. Scripts with a video follow the active project; those without a video are always shown. Also the data side
// of the script editor (tabs, saving the open tab, templates; the actions are in src/stores/scripts.ts, the editor UI in
// src/components/dialogs/ScriptEditor.vue). Stored data unchanged: script.videoId and video.scriptId, script.tabs / activeTab.
import type { ProfileData, Script, ScriptTab, Template, Video } from '../data/types'
import { STATUS_KEYS } from '../data/contentTypes'
import { t, tv } from '../i18n'
import { inActive } from './projects'
import { countWords, spokenSec } from '../utils/words'
import { pubDay } from './published'

/** Plain text of some HTML (same as the legacy plain(): the browser parses it, nothing runs) */
export function plain(html: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = html || ''
  return tpl.content.textContent || ''
}

/** Text of a script: its tabs, or the old single text (script.html, before tabs) */
export const scriptText = (s: Pick<Script, 'tabs' | 'html'>): string => (s.tabs || []).map(tab => plain(tab.html || '')).join(' ') || plain(s.html || '')
/** Number of tabs (an old script without tabs counts as one) */
export const tabCount = (s: Pick<Script, 'tabs'>): number => (s.tabs || []).length || 1

/** Video of a script: the one in script.videoId, or the one that chose it (video.scriptId); null = script without a video */
export const scriptVideo = (d: Pick<ProfileData, 'videos'>, s: Pick<Script, 'id' | 'videoId'>): Video | null =>
  d.videos.find(v => v.id === s.videoId) || d.videos.find(v => v.scriptId === s.id) || null

/** A video in the active project ('all' = every project) */
export const inProject = (active: string, it: { projectId?: string }): boolean => inActive(active, it)   // a call: projects.ts imports this module too

/** Search on title and text; q already lower case */
export const matchesScript = (s: Script, q: string): boolean => !q || ((s.title || '') + ' ' + scriptText(s)).toLowerCase().includes(q)

interface ScriptsView {
  /** Scripts of the page before the filter and the search: those of the active project's videos and those without a video */
  all: Script[]
  /** How many of them have no video */
  orphans: number
  /** Shown list: filter, search, last edited first */
  list: Script[]
  /** The shown scripts whose video is not published yet, and those without a video (last edited first) */
  current: Script[]
  /** The shown scripts of published videos, by year and month (newest first) */
  published: ScriptYear[]
}

/** A video is published when it was marked published by hand (video.published, src/features/published.ts) */
export const isPublishedVideo = (v: Pick<Video, 'published'> | null | undefined): boolean => !!(v && v.published)

/** Month of the "Published" list: key 'YYYY-MM' or 'none' (no date), its scripts newest first */
export interface ScriptMonth {
  k: string
  list: Script[]
}
/** Year of the "Published" list: 'YYYY' or 'none', how many scripts, its months newest first */
export interface ScriptYear {
  y: string
  count: number
  months: ScriptMonth[]
}
/** Day a published script is filed under: the video's release day (pubDay, like the Published page); '' = none */
export function scriptPubDay(v: Pick<Video, 'publishDate' | 'publishedAt'> | null): string {
  return (v && pubDay(v)) || ''
}
/** Release time used to order scripts of the same day: the planned time with a release date, else publishedAt's time */
const pubTime = (v: Pick<Video, 'publishDate' | 'publishTime' | 'publishedAt'> | null): string =>
  !v ? '' : v.publishDate ? (v.publishTime || '') : (v.publishedAt || '').slice(11, 16)
/** Groups the scripts of published videos as year → month → scripts, newest first; scripts without any date go last
 *  in a 'none' year. video(s) = the script's video */
export function groupPublished(scripts: Script[], video: (s: Script) => Video | null): ScriptYear[] {
  const keyed = scripts.map(s => {
    const v = video(s), day = scriptPubDay(v)
    return { s, day, sort: day + ' ' + pubTime(v) + ' ' + String(s.updatedAt || 0).padStart(15, '0') }
  }).sort((a, b) => (!a.day ? 1 : 0) - (!b.day ? 1 : 0) || b.sort.localeCompare(a.sort))
  const years: ScriptYear[] = []
  for (const { s, day } of keyed) {
    const y = day ? day.slice(0, 4) : 'none', k = day ? day.slice(0, 7) : 'none'
    let yr = years.find(x => x.y === y)
    if (!yr) years.push(yr = { y, count: 0, months: [] })
    let mo = yr.months.find(x => x.k === k)
    if (!mo) yr.months.push(mo = { k, list: [] })
    mo.list.push(s)
    yr.count++
  }
  return years
}
/** Key of a month group in the page's open/closed state: 'YYYY/YYYY-MM' ('none/none' = no date) */
export const monthGroupKey = (y: Pick<ScriptYear, 'y'>, m: Pick<ScriptMonth, 'k'>): string => y.y + '/' + m.k
/** Groups open by default in the "Published" list: the most recent year and its most recent month (year key, month key) */
export function defaultOpenGroups(years: ScriptYear[]): string[] {
  const y = years[0]
  return y ? [y.y, monthGroupKey(y, y.months[0])] : []
}

/** The page's list. orphansOnly = "No video" filter */
export function scriptsView(d: ProfileData, active: string, orphansOnly: boolean, query: string): ScriptsView {
  const q = query.trim().toLowerCase()
  const video = new Map(d.scripts.map(s => [s, scriptVideo(d, s)]))   // each script's video, looked up once
  const all = d.scripts.filter(s => { const v = video.get(s); return !v || inProject(active, v) })
  const orphans = all.filter(s => !video.get(s)).length
  const list = all.filter(s => (!orphansOnly || !video.get(s)) && matchesScript(s, q))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
  const done = (s: Script) => isPublishedVideo(video.get(s))
  const current = list.filter(s => !done(s))
  const published = groupPublished(list.filter(done), s => video.get(s) || null)
  return { all, orphans, list, current, published }
}

interface ScriptInfo {
  words: number
  /** Spoken duration: minutes and seconds */
  min: number
  sec: number
  tabs: number
}
export function scriptInfo(s: Script): ScriptInfo {
  const words = countWords(scriptText(s)), sec = spokenSec(words)
  return { words, min: Math.floor(sec / 60), sec: sec % 60, tabs: tabCount(s) }
}

/** Deletes a script; the videos that chose it lose it. false = not found */
export function deleteScript(d: ProfileData, id: string): boolean {
  if (!d.scripts.some(x => x.id === id)) return false
  d.scripts = d.scripts.filter(x => x.id !== id)
  d.videos.forEach(v => { if (v.scriptId === id) v.scriptId = '' })
  return true
}

/** "Attach": the script becomes one of the video's scripts; if the video has no chosen script yet, it becomes its script.
 *  Returns the video, or null when the script or the video is gone */
export function attachScript(d: ProfileData, scriptId: string, videoId: string): Video | null {
  const s = d.scripts.find(x => x.id === scriptId), v = d.videos.find(x => x.id === videoId)
  if (!s || !v) return null
  s.videoId = v.id
  if (!v.scriptId || !d.scripts.some(x => x.id === v.scriptId)) v.scriptId = s.id
  return v
}

/** Videos of the "Attach" dialog (already filtered by the open plan or project): title search, by status then title */
export function linkVideos(videos: Video[], query: string): Video[] {
  const q = query.trim().toLowerCase(), order: readonly string[] = STATUS_KEYS
  return videos.filter(v => !q || (v.title || '').toLowerCase().includes(q))
    .sort((a, b) => order.indexOf(a.status ?? '') - order.indexOf(b.status ?? '') || (a.title || '').localeCompare(b.title || ''))
}
/** Scripts of a video (its own and the chosen one) */
export const videoScriptCount = (d: Pick<ProfileData, 'scripts'>, v: Pick<Video, 'id' | 'scriptId'>): number =>
  videoScripts(d, v).length

/** Start of a template's text, for its row */
export const templatePreview = (tpl: Pick<Template, 'html'>): string => plain(tpl.html || '').slice(0, 90)
/** Renames a template (name trimmed); false = empty name or template gone */
export function renameTemplate(d: ProfileData, id: string, name: string | null): boolean {
  const n = (name || '').trim(), tpl = d.templates.find(x => x.id === id)
  if (!n || !tpl) return false
  tpl.name = n
  return true
}
/** Deletes a template (scripts created from it are not touched); false = not found */
export function deleteTemplate(d: ProfileData, id: string): boolean {
  if (!d.templates.some(x => x.id === id)) return false
  d.templates = d.templates.filter(x => x.id !== id)
  return true
}

// ===== Script editor: tabs (drafts), saving, templates (ported from the legacy editor/scripts.js; golden tests against
// its verbatim copy in tests/fixtures/legacy/scripts.js, tests/script-editor.test.ts) =====

/** A script with its tabs: an old script without tabs gets one ("First draft") with its old text (script.html, kept);
 *  an active tab that is gone becomes the first one. In place; returns the same script */
export function ensureTabs(sc: Script, now = Date.now()): Script & { tabs: ScriptTab[] } {
  if (!sc.tabs || !sc.tabs.length) sc.tabs = [{ id: 'tb_' + now, name: t('First draft'), html: sc.html || '' }]
  if (!sc.tabs.some(tab => tab.id === sc.activeTab)) sc.activeTab = sc.tabs[0].id
  return sc as Script & { tabs: ScriptTab[] }
}
/** The open tab of a script that has its tabs (ensureTabs) */
export const activeTabOf = (sc: Script): ScriptTab | undefined => (sc.tabs || []).find(tab => tab.id === sc.activeTab)
/** HTML of the tab shown (the open one, otherwise the first), or the old single text */
export function shownHtml(sc: Script): string {
  const tab = activeTabOf(sc) || (sc.tabs || [])[0]
  return tab ? tab.html : (sc.html || '')
}
/** The scripts of a video: those that belong to it, and the one it chose (older data) */
export const videoScripts = (d: Pick<ProfileData, 'scripts'>, v: Pick<Video, 'id' | 'scriptId'>): Script[] =>
  (d.scripts || []).filter(x => x.videoId === v.id || x.id === v.scriptId)
/** The script open in the editor: [data, script] (script undefined when it is gone or nothing is open) */
export const scriptOf = (d: ProfileData, id: string | null): [ProfileData, Script | undefined] => [d, d.scripts.find(x => x.id === id)]

/** What the editor holds: the title field, the open tab's HTML (already cleaned by the editor) and the mode */
export interface EditorText {
  title: string
  html: string
  mode: 'visual' | 'md' | 'fountain'
}
/** Writes the editor's text into the script: title (empty = "Untitled script"), the open tab's HTML, mode, last edit */
export function writeScript(sc: Script, text: EditorText, now = Date.now()): void {
  ensureTabs(sc, now)
  sc.title = text.title.trim() || t('Untitled script')
  activeTabOf(sc)!.html = text.html
  sc.mode = text.mode
  sc.updatedAt = now
}
/** Name proposed for a new tab: "Draft <n>", n = tabs + 1 */
export const newTabName = (sc: Script): string => t('Draft {n}', { n: (sc.tabs || []).length + 1 })
/** Adds an empty tab and opens it; false = empty name */
export function addTab(sc: Script, name: string | null, now = Date.now()): boolean {
  if (!name || !name.trim()) return false
  const id = 'tb_' + now
  ;(sc.tabs = sc.tabs || []).push({ id, name: name.trim(), html: '' })
  sc.activeTab = id
  return true
}
/** Renames a tab (trimmed, max 40 characters in the field); false = no change (empty, same name as shown, tab gone) */
export function renameTab(sc: Script, id: string, name: string | null): boolean {
  const n = (name || '').trim(), tab = (sc.tabs || []).find(x => x.id === id)
  if (!tab || !n || n === tv(tab.name)) return false
  tab.name = n
  return true
}
/** Deletes a tab and the comments written on it (they could never be seen again); the open tab, if deleted, becomes
 *  the first one. false = the tab is gone or it is the only one (a script always keeps one tab) */
export function deleteTab(sc: Script, id: string): boolean {
  const tabs = sc.tabs || []
  if (!tabs.some(x => x.id === id) || tabs.length < 2) return false
  sc.tabs = tabs.filter(x => x.id !== id)
  if (sc.activeTab === id) sc.activeTab = sc.tabs[0].id
  if (Array.isArray(sc.comments) && sc.comments.some(c => c.tabId === id)) sc.comments = sc.comments.filter(c => c.tabId !== id)
  return true
}
/** "Save as template": a new template with the open tab's HTML (and the Fountain mode, for a screenplay); null = empty name */
export function templateFrom(sc: Script, name: string | null, now = Date.now()): Template | null {
  if (!name || !name.trim()) return null
  const tpl: Template = { id: 'tp_' + now, name: name.trim(), html: activeTabOf(sc)!.html }
  if (sc.mode === 'fountain') tpl.mode = 'fountain'
  return tpl
}
